import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { forIn, get, isEmpty, isNumber, isString, last, sortBy, trimStart } from 'lodash';
import moment from 'moment-timezone';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useEventStream } from '../Hooks/useEventStream';

moment.updateLocale('en', {
  relativeTime: {
    future: '%s',
    past: '%s',
    s: 'Just Now',
    ss: 'Just Now',
    m: '1 min ago',
    mm: '%d min ago',
    h: '1 hour ago',
    hh: '%d hours ago',
    d: '1 day ago',
    dd: '%d days ago',
    w: '1 week ago',
    ww: '%d weeks ago',
    M: '1 month ago',
    MM: '%d months ago',
    y: '1 year ago',
    yy: '%d years ago',
  },
});

// Helper function to check if the user has Staff or Staff Admin privileges
export const hasStaffPrivileges = (userRole) => {
  return userRole === 'Staff' || userRole === 'Staff Admin';
};

// Staff users acting on care hubs should not be allowed to unpair devices.
export const canShowUnpairAction = (userRole, hubRoleName) => {
  if (!hasStaffPrivileges(userRole)) return false;

  const normalizedHubRole = String(hubRoleName || '')
    .trim()
    .toLowerCase();
  // Fail closed until hub role is known to avoid briefly exposing unpair actions.
  if (!normalizedHubRole) return false;

  return normalizedHubRole !== 'care';
};

// Hook to get user role
export const useUserRole = () => {
  const currentUser = useSelector((state) => state.auth.currentUser);
  const userRole = currentUser?.permission_level;
  return userRole;
};

// Helper function to check if the user has only Staff Admin privileges
export const hasOnlyStaffAdminPrivileges = (userRole) => {
  return userRole === 'Staff Admin';
};

export const RCButtons = {
  RCTV: 'TV',
  RCMicrophone: 'Microphone',
  RCPower: 'Power',
  RCBack: 'Back',
  RCHome: 'Home',
  RCVolumeUp: 'Volume Up',
  RCAcceptCall: 'Accept Call',
  RCVolumeDown: 'Volume Down',
  RCDeclineCall: 'Decline Call',
  RCArrowUp: 'Arrow Up',
  RCArrowDown: 'Arrow Down',
  RCArrowLeft: 'Arrow Left',
  RCArrowRight: 'Arrow Right',
  RCOK: 'OK',
};

export const RCKeyButtonMap = {
  KEYCODE_POWER: RCButtons.RCPower,
  KEYCODE_TV: RCButtons.RCTV,
  KEYCODE_SEARCH: RCButtons.RCMicrophone,
  KEYCODE_DPAD_UP: RCButtons.RCArrowUp,
  KEYCODE_DPAD_DOWN: RCButtons.RCArrowDown,
  KEYCODE_DPAD_LEFT: RCButtons.RCArrowLeft,
  KEYCODE_DPAD_RIGHT: RCButtons.RCArrowRight,
  KEYCODE_DPAD_CENTER: RCButtons.RCOK,
  KEYCODE_BACK: RCButtons.RCBack,
  KEYCODE_F4: RCButtons.RCHome,
  KEYCODE_VOLUME_UP: RCButtons.RCVolumeUp,
  KEYCODE_VOLUME_DOWN: RCButtons.RCVolumeDown,
  KEYCODE_HELP: RCButtons.RCAcceptCall,
  KEYCODE_MEDIA_STOP: RCButtons.RCDeclineCall,
};

export const rcEventTypes = {
  KEY_INPUT_EVENT: 'key_input_event',
  RCU_MOTION_EVENT: 'rcu_motion_event',
};

export const rcEventDataKeys = {
  MESSAGE_TYPE: 'message_type',
  DEVICE_UUID: 'deviceuuid',
  TIMESTAMP: 'utc_timestamp_inms',
  MOTION_TIME: 'motion_time',
  ORIENTATION: 'orientation',
};

export const useUserEvents = (device, hubRecord, setActionAndOrientation) => {
  const userEventsApi = useSelector((state) => state.apis.userEventsApi);
  const trans = useSelector(translator);

  const processUserEventsData = (eventsData) => {
    setActionAndOrientation((prev) => {
      if (isEmpty(eventsData)) return prev;

      const filteredEventsData = eventsData.filter(
        (e) =>
          e[rcEventDataKeys.DEVICE_UUID] === device.deviceuuid &&
          Object.values(rcEventTypes).includes(e[rcEventDataKeys.MESSAGE_TYPE])
      );
      if (isEmpty(filteredEventsData)) return prev;

      let action = prev.action;
      let actionTimestamp = prev.actionTimestamp;
      let key = prev.key;
      let orientation = prev.orientation;
      forIn(rcEventTypes, (eventType) => {
        const eventsOfType = filteredEventsData.filter(
          (e) => e[rcEventDataKeys.MESSAGE_TYPE] === eventType
        );
        const sortedEvents = sortBy(eventsOfType, (e) =>
          moment(e[rcEventDataKeys.TIMESTAMP]).valueOf()
        );
        const latestEvent = last(sortedEvents);

        switch (eventType) {
          case rcEventTypes.KEY_INPUT_EVENT: {
            action = get(latestEvent, `${rcEventTypes.KEY_INPUT_EVENT}__action`, prev.action);
            action = trimStart(action, 'key_');
            actionTimestamp = get(latestEvent, rcEventDataKeys.TIMESTAMP, prev.actionTimestamp);
            key = get(latestEvent, `${rcEventTypes.KEY_INPUT_EVENT}__name`, prev.key);
            // key = trimStart(key, 'KEYCODE_');
            break;
          }

          case rcEventTypes.RCU_MOTION_EVENT: {
            const events = get(latestEvent, `${rcEventTypes.RCU_MOTION_EVENT}__motion_events`, []);
            if (isEmpty(events)) break;
            const sortedEvents = sortBy(events, [rcEventDataKeys.MOTION_TIME]);
            const latestItem = last(sortedEvents);
            orientation = get(latestItem, rcEventDataKeys.ORIENTATION, prev.orientation);
            break;
          }

          default:
            break;
        }
      });

      return { action, actionTimestamp, key, orientation };
    });
  };

  useEffect(() => {
    const errorCallback = (error) => {
      console.error(error);
      processUserEventsData([]);
    };

    const successCallback = (data) => processUserEventsData(data['user_events']);

    try {
      userEventsApi.getLatestUserEvents(
        hubRecord.useruuid,
        {
          deviceuuid: [device.deviceuuid],
          eventtypes: [...Object.values(rcEventTypes)],
        },
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  }, [hubRecord.useruuid, device.deviceuuid, trans, userEventsApi]);
};

export const useRCEventStream = (hubRecord, eventTypes, setActionAndOrientation) => {
  const { latestEvent } = useEventStream(hubRecord, eventTypes);

  const processEventFromStream = (latestEvent) => {
    if (isEmpty(latestEvent)) return;

    setActionAndOrientation((prev) => {
      let action = prev.action;
      let actionTimestamp = prev.actionTimestamp;
      let key = prev.key;
      let orientation = prev.orientation;

      switch (get(latestEvent, rcEventDataKeys.MESSAGE_TYPE, '')) {
        case rcEventTypes.KEY_INPUT_EVENT: {
          action = get(latestEvent, `${rcEventTypes.KEY_INPUT_EVENT}__action`, prev.action);
          action = trimStart(action, 'key_');
          actionTimestamp = get(latestEvent, rcEventDataKeys.TIMESTAMP, prev.actionTimestamp);
          key = get(latestEvent, `${rcEventTypes.KEY_INPUT_EVENT}__name`, prev.key);
          // key = trimStart(key, 'KEYCODE_');
          break;
        }

        case rcEventTypes.RCU_MOTION_EVENT: {
          const events = get(latestEvent, `${rcEventTypes.RCU_MOTION_EVENT}__motion_events`, []);
          if (isEmpty(events)) break;
          const sortedEvents = sortBy(events, [rcEventDataKeys.MOTION_TIME]);
          const latestItem = last(sortedEvents);
          orientation = get(latestItem, rcEventDataKeys.ORIENTATION, prev.orientation);
          break;
        }

        default:
          break;
      }

      return { action, actionTimestamp, key, orientation };
    });
  };

  useEffect(() => {
    processEventFromStream(latestEvent);
  }, [latestEvent]);
};

export const formatTimestamp = (timestamp, timezone, userLocale) => {
  if ((isString(timestamp) && isEmpty(timestamp)) || (isNumber(timestamp) && timestamp <= 0))
    return '';

  const inputTime = moment(timestamp).tz(timezone);
  let timeDiff = '';
  if (userLocale) {
    timeDiff = inputTime.locale(userLocale).fromNow();
  } else {
    timeDiff = inputTime.fromNow();
  }

  return timeDiff;
};

// Function to format timestamp for insight alert notification display
export const formatTimestampForAlertNotificationDisplay = (
  timestamp,
  timezone = 'UTC',
  userLocale = 'en-US'
) => {
  if ((isString(timestamp) && isEmpty(timestamp)) || (isNumber(timestamp) && timestamp <= 0))
    return '';

  const dateTime = moment(timestamp).tz(timezone).locale(userLocale);
  if (!dateTime.isValid()) {
    console.warn('Invalid timestamp in alert notification:', timestamp);
    return String(timestamp);
  }

  return dateTime.format('LLLL zz');
};
